import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  Eye,
  Bot,
  Loader2,
  Tag,
} from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useToast } from '@/hooks/useToast';
import ReactMarkdown from 'react-markdown';

interface Categoria {
  id: string;
  nome: string;
  slug: string;
}

interface BlogPost {
  id: string;
  titulo: string;
  slug: string;
  resumo: string;
  conteudo: string;
  imagemCapa: string | null;
  metaTitle: string;
  metaDescription: string;
  palavrasChave: string[];
  tempoLeitura: number;
  status: 'PUBLICADO' | 'RASCUNHO' | 'AGENDADO';
  categoriaId: string;
  permiteComentarios: boolean;
  destaque: boolean;
}

export default function BlogEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEdicao = !!id;

  const [form, setForm] = useState<Partial<BlogPost>>({
    titulo: '',
    resumo: '',
    conteudo: '',
    imagemCapa: '',
    metaTitle: '',
    metaDescription: '',
    palavrasChave: [],
    tempoLeitura: 5,
    status: 'RASCUNHO',
    categoriaId: '',
    permiteComentarios: true,
    destaque: false,
  });

  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [gerandoComIA, setGerandoComIA] = useState(false);
  const [preview, setPreview] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    carregarCategorias();
    if (isEdicao) {
      carregarPost();
    }
  }, [id]);

  const carregarCategorias = async () => {
    try {
      const { data } = await api.get('/blog/categorias');
      setCategorias(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const carregarPost = async () => {
    try {
      setLoading(true);
      // Usar endpoint público para buscar post
      const { data } = await api.get(`/blog/posts/${id}`);
      setForm(data.post);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar o post.',
        variant: 'destructive',
      });
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvar = async () => {
    try {
      setSalvando(true);

      if (!form.titulo || !form.conteudo || !form.categoriaId) {
        toast({
          title: 'Campos obrigatórios',
          description: 'Preencha o título, conteúdo e categoria.',
          variant: 'destructive',
        });
        return;
      }

      if (isEdicao) {
        await api.put(`/blog/posts/${id}`, form);
      } else {
        await api.post('/blog/posts', form);
      }

      toast({
        title: 'Sucesso',
        description: isEdicao
          ? 'Artigo atualizado com sucesso!'
          : 'Artigo criado com sucesso!',
      });

      navigate('/admin/blog');
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar o artigo.',
        variant: 'destructive',
      });
    } finally {
      setSalvando(false);
    }
  };

  const gerarComIA = async () => {
    if (!form.titulo) {
      toast({
        title: 'Título necessário',
        description: 'Digite um título para gerar o conteúdo.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setGerandoComIA(true);
      const { data } = await api.post('/blog/gerar-conteudo', {
        titulo: form.titulo,
        categoria: categorias.find((c) => c.id === form.categoriaId)?.nome,
      });

      setForm({
        ...form,
        conteudo: data.conteudo,
        resumo: data.resumo,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        palavrasChave: data.palavrasChave,
        tempoLeitura: data.tempoLeitura,
      });

      toast({
        title: 'Sucesso',
        description: 'Conteúdo gerado com IA!',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível gerar o conteúdo.',
        variant: 'destructive',
      });
    } finally {
      setGerandoComIA(false);
    }
  };

  const adicionarTag = () => {
    if (tagInput && !form.palavrasChave?.includes(tagInput)) {
      setForm({
        ...form,
        palavrasChave: [...(form.palavrasChave || []), tagInput],
      });
      setTagInput('');
    }
  };

  const removerTag = (tag: string) => {
    setForm({
      ...form,
      palavrasChave: form.palavrasChave?.filter((t) => t !== tag) || [],
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#FF6B35]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/blog')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <h1 className="text-xl font-bold text-[#1a1a2e]">
                {isEdicao ? 'Editar Artigo' : 'Novo Artigo'}
              </h1>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setPreview(!preview)}
              >
                <Eye className="w-4 h-4 mr-2" />
                {preview ? 'Editar' : 'Preview'}
              </Button>
              <Button
                variant="outline"
                onClick={gerarComIA}
                disabled={gerandoComIA}
              >
                <Bot
                  className={`w-4 h-4 mr-2 ${
                    gerandoComIA ? 'animate-spin' : ''
                  }`}
                />
                {gerandoComIA ? 'Gerando...' : 'Gerar com IA'}
              </Button>
              <Button variant="nuvem" onClick={handleSalvar} disabled={salvando}>
                <Save className="w-4 h-4 mr-2" />
                {salvando ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Coluna principal */}
          <div className="lg:col-span-2 space-y-6">
            {preview ? (
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h1 className="text-3xl font-bold mb-4">{form.titulo}</h1>
                <div className="prose prose-lg max-w-none">
                  <ReactMarkdown>{form.conteudo || ''}</ReactMarkdown>
                </div>
              </div>
            ) : (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 shadow-sm space-y-6"
                >
                  <div>
                    <Label htmlFor="titulo">Título *</Label>
                    <Input
                      id="titulo"
                      value={form.titulo}
                      onChange={(e) =>
                        setForm({ ...form, titulo: e.target.value })
                      }
                      placeholder="Título do artigo"
                    />
                  </div>

                  <div>
                    <Label htmlFor="resumo">Resumo *</Label>
                    <textarea
                      id="resumo"
                      value={form.resumo}
                      onChange={(e) =>
                        setForm({ ...form, resumo: e.target.value })
                      }
                      placeholder="Breve descrição do artigo"
                      rows={3}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <Label htmlFor="conteudo">Conteúdo *</Label>
                    <textarea
                      id="conteudo"
                      value={form.conteudo}
                      onChange={(e) =>
                        setForm({ ...form, conteudo: e.target.value })
                      }
                      placeholder="Conteúdo em Markdown..."
                      rows={20}
                      className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Suporta formatação Markdown
                    </p>
                  </div>
                </motion.div>

                {/* SEO */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm space-y-6"
                >
                  <h3 className="font-semibold text-lg">SEO</h3>

                  <div>
                    <Label htmlFor="metaTitle">Meta Title</Label>
                    <Input
                      id="metaTitle"
                      value={form.metaTitle}
                      onChange={(e) =>
                        setForm({ ...form, metaTitle: e.target.value })
                      }
                      placeholder="Título para SEO (até 60 caracteres)"
                      maxLength={60}
                    />
                  </div>

                  <div>
                    <Label htmlFor="metaDescription">Meta Description</Label>
                    <textarea
                      id="metaDescription"
                      value={form.metaDescription}
                      onChange={(e) =>
                        setForm({ ...form, metaDescription: e.target.value })
                      }
                      placeholder="Descrição para SEO (até 160 caracteres)"
                      rows={3}
                      maxLength={160}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                    />
                  </div>
                </motion.div>
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-sm space-y-6"
            >
              <h3 className="font-semibold text-lg">Configurações</h3>

              <div>
                <Label htmlFor="categoria">Categoria *</Label>
                <select
                  id="categoria"
                  value={form.categoriaId}
                  onChange={(e) =>
                    setForm({ ...form, categoriaId: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                >
                  <option value="">Selecione...</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  value={form.status}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      status: e.target.value as BlogPost['status'],
                    })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                >
                  <option value="RASCUNHO">Rascunho</option>
                  <option value="PUBLICADO">Publicado</option>
                  <option value="AGENDADO">Agendado</option>
                </select>
              </div>

              <div>
                <Label htmlFor="imagemCapa">URL da Imagem de Capa</Label>
                <Input
                  id="imagemCapa"
                  value={form.imagemCapa || ''}
                  onChange={(e) =>
                    setForm({ ...form, imagemCapa: e.target.value })
                  }
                  placeholder="https://..."
                />
              </div>

              <div>
                <Label htmlFor="tempoLeitura">Tempo de Leitura (min)</Label>
                <Input
                  id="tempoLeitura"
                  type="number"
                  min={1}
                  max={60}
                  value={form.tempoLeitura}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      tempoLeitura: parseInt(e.target.value) || 5,
                    })
                  }
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="destaque"
                  checked={form.destaque}
                  onChange={(e) =>
                    setForm({ ...form, destaque: e.target.checked })
                  }
                  className="w-4 h-4 rounded"
                />
                <Label htmlFor="destaque" className="mb-0">
                  Destacar na home
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="comentarios"
                  checked={form.permiteComentarios}
                  onChange={(e) =>
                    setForm({ ...form, permiteComentarios: e.target.checked })
                  }
                  className="w-4 h-4 rounded"
                />
                <Label htmlFor="comentarios" className="mb-0">
                  Permitir comentários
                </Label>
              </div>
            </motion.div>

            {/* Palavras-chave */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-6 shadow-sm space-y-4"
            >
              <h3 className="font-semibold text-lg">Palavras-chave</h3>

              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Adicionar tag..."
                  onKeyPress={(e) => e.key === 'Enter' && adicionarTag()}
                />
                <Button type="button" onClick={adicionarTag}>
                  <Tag className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {form.palavrasChave?.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-[#FF6B35]/10 text-[#FF6B35] rounded-full text-sm flex items-center gap-2"
                  >
                    {tag}
                    <button
                      onClick={() => removerTag(tag)}
                      className="hover:text-[#FF6B35]/80"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
